import { ChangeEvent, FormEvent, useState } from 'react'

import { Button, Input, LinkButton, spacings, Typography } from '@guidde/design-system/legacy'
import { Box, Stack } from '@mui/material'

import { links } from 'app/links'
import { QuizIdentificationMode, QuizLearnerIdentityType } from 'app/types'
import { isValidEmail } from 'modules/email'

const MAX_EMAIL_LENGTH = 254
const MAX_NAME_LENGTH = 200

type Props = {
    mode: QuizIdentificationMode
    onIdentify: (_identity: QuizLearnerIdentityType) => void
    onContinueWithoutIdentity: () => void
    initialIdentity?: QuizLearnerIdentityType
    isSkipAllowed?: boolean
}

export const QuizIdentificationCard = ({
    mode,
    onIdentify,
    onContinueWithoutIdentity,
    initialIdentity,
    isSkipAllowed = true,
}: Props) => {
    const [email, setEmail] = useState(initialIdentity?.email ?? '')
    const [name, setName] = useState(initialIdentity?.name ?? '')
    const [isEmailTouched, setIsEmailTouched] = useState(false)

    const trimmedEmail = email.trim()
    const trimmedName = name.trim()
    const isEmailInvalid = Boolean(trimmedEmail) && !isValidEmail(trimmedEmail)
    const canSubmit = Boolean(trimmedEmail || trimmedName) && !isEmailInvalid
    const showEmailError = isEmailTouched && isEmailInvalid

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        if (!canSubmit) return

        onIdentify({ ...(trimmedEmail && { email: trimmedEmail }), ...(trimmedName && { name: trimmedName }) })
    }

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            height="100%"
            px={spacings.xl}
            py={spacings['3xl']}
            sx={{ overflowY: 'auto' }}
        >
            <Stack
                component="form"
                noValidate
                onSubmit={handleSubmit}
                width="100%"
                maxWidth="sm"
                gap={spacings.xl}
            >
                <Stack
                    alignItems="center"
                    gap={spacings.sm}
                    textAlign="center"
                >
                    <Typography
                        variant="heading"
                        size="lg"
                        fontWeight="bold"
                        color="grey.900"
                    >
                        Before we continue
                    </Typography>
                    <Typography
                        variant="text"
                        size="md"
                        color="grey.600"
                    >
                        Enter your email or full name to start the quiz.
                    </Typography>
                </Stack>

                <Stack gap={spacings.lg}>
                    <Input
                        label="Email"
                        placeholder="Email"
                        value={email}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                        onBlur={() => setIsEmailTouched(true)}
                        errorText={showEmailError ? 'Enter a valid email' : ''}
                        inputProps={{
                            type: 'email',
                            autoComplete: 'email',
                            maxLength: MAX_EMAIL_LENGTH,
                            'data-testid': 'quiz-identification-email-input',
                            'aria-invalid': showEmailError,
                        }}
                    />
                    <Input
                        label="Name"
                        placeholder="Full Name"
                        value={name}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        inputProps={{
                            autoComplete: 'name',
                            maxLength: MAX_NAME_LENGTH,
                            'data-testid': 'quiz-identification-name-input',
                        }}
                    />
                </Stack>

                <Stack
                    alignItems="center"
                    gap={spacings.md}
                >
                    <Button
                        data-testid="quiz-identification-start-button"
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!canSubmit}
                    >
                        Start quiz
                    </Button>
                    {isSkipAllowed && (
                        <Button
                            data-testid="quiz-identification-continue-button"
                            variant="text"
                            color="secondary"
                            onClick={onContinueWithoutIdentity}
                        >
                            {mode === 'optional' ? 'Continue as guest' : 'Continue without quiz'}
                        </Button>
                    )}
                </Stack>

                <Box textAlign="center">
                    <Typography
                        variant="text"
                        size="sm"
                        color="grey.500"
                    >
                        By continuing, you agree to our{' '}
                        <LinkButton
                            data-testid="quiz-identification-privacy-link"
                            href={links.privacyPolicy}
                            target="_blank"
                            size="small"
                            hasPadding={false}
                            aria-label="Privacy Policy (opens in a new tab)"
                        >
                            Privacy Policy
                        </LinkButton>
                        .
                    </Typography>
                </Box>
            </Stack>
        </Stack>
    )
}
